import { Typography } from '@mui/material'
import { CarrierVisitStatus, OperationType, WorkInstructionDto } from '@operations/app/api'
import { useOperationsStore } from '@operations/AppProvider'
import { PriorityChip } from '@operations/components/chips/PriorityChip'
import { WorkInstructionStatusChip } from '@operations/components/chips/WorkInstructionStatusChip'
import { WorkInstructionMenuOptions } from '@operations/features/workInstructions/components/atoms/WorkInstructionMenuOptions'
import { WorkInstructionTableDate } from '@operations/features/workInstructions/components/atoms/WorkInstructionTableDate'
import {
  canStartWorkInstruction,
  canStopWorkInstruction,
} from '@operations/features/workInstructions/helpers/work-instruction-table.helper'
import { workInstructionColumnFilterPresets } from '@operations/features/workInstructions/tableConfigs/workIntructionColumnFilterConfigs'
import { useTranslate } from '@tolgee/react'
import {
  EmptyIndicator,
  getMultiSelectRenderBadge,
  TomTable,
  TomTableEmptyCell,
  useTomTable,
  YesNoBadge,
} from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { TomTableToolbarAlertBanner } from 'modules/ui/src/theme/components/table/organisms/TomTableToolbarAlertBanner'
import { useCallback, useMemo } from 'react'
import { useWorkQueueInstructionTableActions } from '../../hooks/useWorkQueueInstructionTableActions'
import { WorkQueue } from '../../models/work-queue.model'

interface Props {
  workQueue: WorkQueue
  workInstructions: WorkInstructionDto[]
  vesselName?: string | null
  vesselVisitStatus?: CarrierVisitStatus
  getWorkInstructionForRestow: (
    containerNumber: string,
    operationType: OperationType,
  ) => WorkInstructionDto | undefined
}
export const WorkQueueInstructionsTable = observer(
  ({
    workQueue,
    workInstructions,
    vesselName,
    vesselVisitStatus,
    getWorkInstructionForRestow,
  }: Props) => {
    const { t } = useTranslate()
    const { craneSplitDialogUIStore } = useOperationsStore()

    const getTableActions = useWorkQueueInstructionTableActions(craneSplitDialogUIStore)

    const computePlannedTime = useCallback(
      (row: WorkInstructionDto) => {
        if (!workQueue || !row.workQueueOrder) return null

        return dayjs(workQueue.startTime)
          .add((row.workQueueOrder ?? 0) * workQueue.processingTime, 'seconds')
          .toString()
      },
      [workQueue],
    )

    const getDestination = (row: WorkInstructionDto) => {
      if (row.cargoUnit?.category === 'Restow') {
        const outboundWI = getWorkInstructionForRestow(
          row.cargoUnit?.containerNumber ?? '',
          'Outbound',
        )
        if (row.operationType === 'Inbound' && ['Todo', 'Pending'].includes(row.status)) {
          return t('vesselFullName', 'Vessel {name}', { name: vesselName }) ?? EmptyIndicator
        }
      }
      return row.destination ? t(row.destination) : EmptyIndicator
    }

    const getOrigin = (row: WorkInstructionDto) => {
      if (row.cargoUnit?.category === 'Restow') {
        const inboundWI = getWorkInstructionForRestow(
          row.cargoUnit?.containerNumber ?? '',
          'Inbound',
        )
        if (
          row.operationType === 'Outbound' &&
          inboundWI?.status &&
          ['Todo', 'Pending'].includes(inboundWI.status)
        ) {
          return t('vesselFullName', 'Vessel {name}', { name: vesselName }) ?? EmptyIndicator
        }
      }
      return row.origin ? t(row.origin) : EmptyIndicator
    }

    const columns = useMemo<MRT_ColumnDef<WorkInstructionDto>[]>(
      () => [
        {
          ...workInstructionColumnFilterPresets.searchMandatory(),
          accessorKey: 'cargoUnit.containerNumber',
          header: t('unit', 'Unit'),
          size: 170,
          Cell: cell => <Typography variant='subtitle2'>{cell.renderedCellValue}</Typography>,
        },
        {
          accessorKey: 'workQueueOrder',
          header: t('sequence', 'Sequence'),
          size: 120,
          ...workInstructionColumnFilterPresets.numericMandatory(),
          Cell: TomTableEmptyCell,
        },
        {
          accessorKey: 'origin',
          header: t('origin', 'Origin'),
          accessorFn: row => getOrigin(row),
          size: 200,
          ...workInstructionColumnFilterPresets.searchMandatory(),
          enableSorting: false,
        },
        {
          accessorKey: 'destination',
          header: t('destination', 'Destination'),
          accessorFn: row => getDestination(row),
          size: 200,
          ...workInstructionColumnFilterPresets.searchMandatory(),
          enableSorting: false,
        },
        {
          header: t('plannedTime', 'Planned time'),
          accessorFn: row => computePlannedTime(row),
          Cell: cell => <WorkInstructionTableDate date={computePlannedTime(cell.row.original)} />,
          size: 120,
          enableSorting: false,
          ...workInstructionColumnFilterPresets.dateMandatory(),
        },

        {
          accessorKey: 'hasActiveHolds',
          ...workInstructionColumnFilterPresets.yesNoSelect(),
          header: t('hold', 'Hold'),
          size: 170,
          accessorFn: row => {
            return row.hasActiveHolds ? 'Yes' : 'No'
          },
          Cell: cell => {
            return (
              <YesNoBadge
                isYes={cell.row.original.hasActiveHolds}
                isWarning={cell.row.original.hasActiveHolds}
              />
            )
          },
          enableSorting: true,
        },

        {
          accessorKey: 'status',
          ...workInstructionColumnFilterPresets.statusMultiSelect(t),
          header: t('status', 'Status'),
          Cell: cell => <WorkInstructionStatusChip status={cell.row.original.status} />,
          size: 120,
          enableSorting: false,
          muiFilterTextFieldProps: ({ column }) =>
            getMultiSelectRenderBadge(column, t, t('filterByStatus', 'Filter by Status')),
        },

        {
          accessorKey: 'isPriority',
          ...workInstructionColumnFilterPresets.priorityMultiSelect(t),
          header: t('priority', 'Priority'),
          Cell: cell => <PriorityChip isPriority={cell.row.original.isPriority} />,
          size: 120,
          enableSorting: true,
          muiFilterTextFieldProps: ({ column }) =>
            getMultiSelectRenderBadge(column, t, t('filterByPriority', 'Filter by Priority')),
        },
      ],
      [computePlannedTime, t],
    )

    const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig(
      'operations/work-queue-instructions',
    )

    const table = useTomTable<WorkInstructionDto>(
      {
        data: workInstructions,
        columns: columns,
        enableExpanding: false,
        enablePagination: false,
        enableBottomToolbar: false,
        enableRowSelection:
          vesselVisitStatus === CarrierVisitStatus.InOperation
            ? row =>
                canStopWorkInstruction(row.original.status) ||
                canStartWorkInstruction(row.original.status)
            : false,
        enableRowActions: vesselVisitStatus === CarrierVisitStatus.InOperation,
        muiTableContainerProps: { sx: { maxHeight: '90dvh' } },
        renderRowActions: ({ row }) => (
          <WorkInstructionMenuOptions
            type='craneSplit'
            workInstruction={row.original}
            uiStore={craneSplitDialogUIStore}
          />
        ),
        getRowId: row => row.id.toString(),
        renderToolbarAlertBannerContent: ({ table }) => {
          const selectedRows = table.getSelectedRowModel().rows
          const dynamicActions = getTableActions(selectedRows)

          if (!dynamicActions.length) {
            return null
          }

          return <TomTableToolbarAlertBanner table={table} tableActions={dynamicActions} />
        },
      },
      undefined,
      undefined,
      { columns, savedPreferences, onPreferencesChange },
    )
    return <TomTable table={table} />
  },
)
